import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ArrowRight, ArrowLeft, ShieldCheck, Lock, Zap,
  FileText, Clock, Users, TrendingUp, HelpCircle, Gift, Sparkles,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { sendQuizFunnelEvent, quizIdentity } from "../lib/pageviewTracker";
import { useSeo } from "../lib/seo";
import QuizResult from "../components/quiz/QuizResult";
import QuizSoftReveal from "../components/quiz/QuizSoftReveal";
import CountdownStart from "../components/quiz/CountdownStart";
import { PaidIntro } from "../components/quiz/PaidIntro";
import ExitRescueModal from "../components/quiz/ExitRescueModal";

// Brand accent for the quiz surface — Warm Amber from the persona palette.
const GOLD = "#D49B3C";
const GOLD_DARK = "#A9712A";

/* Fisher–Yates copy shuffle. */
function shuffled(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* Anti-memorization: randomize question order AND answer-choice order on
   every take, so repeat visitors (e.g. next month's contest) can't replay
   remembered positions. Each choice keeps its `origIdx` so the submission
   still maps to the backend's canonical scoring order. */
function shuffleForDisplay(qs) {
  return shuffled(
    qs.map((q) => ({
      ...q,
      answers: shuffled(q.answers.map((a, origIdx) => ({ ...a, origIdx }))),
    })),
  );
}

const TAKEN_KEY = "fh_quiz_taken";

function readTakenRecord() {
  try {
    const rec = JSON.parse(localStorage.getItem(TAKEN_KEY) || "null");
    if (rec?.month === new Date().toISOString().slice(0, 7)) return rec;
  } catch {
    // corrupt record — treat as absent
  }
  return null;
}

/* Static persona list for the contest guess picker — identity data only,
   the full persona payload still comes from the backend after scoring. */
const PERSONA_OPTIONS = [
  { slug: "architect", name: "The Architect", icon: "🏛️", hint: "Plans it all" },
  { slug: "explorer", name: "The Explorer", icon: "🧭", hint: "Chases the new" },
  { slug: "guardian", name: "The Guardian", icon: "🛡️", hint: "Protects the nest" },
  { slug: "connector", name: "The Connector", icon: "🤝", hint: "Leads with heart" },
  { slug: "dreamer", name: "The Dreamer", icon: "✨", hint: "Sees the future" },
  { slug: "minimalist", name: "The Minimalist", icon: "🧘", hint: "Keeps it simple" },
  { slug: "peacekeeper", name: "The Peacekeeper", icon: "☮️", hint: "Keeps the peace" },
  { slug: "achiever", name: "The Achiever", icon: "🏆", hint: "Plays to win" },
];
const NAVY = "#102945";

/**
 * MoneyMind™ Quiz — three-phase single-page experience.
 *
 *   Phase 1: Intro card with eyebrow badge + meta row + "Discover My MoneyMind" CTA.
 *   Phase 2: Wizard — one question at a time, progress bar across the top,
 *            Back/Next nav along the bottom of the card.
 *   Phase 3: Result reveal (separate <QuizResult /> component).
 *
 * Public route — works for anonymous lead-gen visitors AND inside the app
 * shell (the inner content renders the same; the outer chrome differs).
 */
export default function Quiz() {
  useSeo({
    title: "The MoneyMind Quiz™ — Discover Your Money Persona (Free)",
    description: "20 questions, about 5 minutes. Find your MoneyMind persona, the hidden pattern behind your money decisions, and three habits tuned to how you actually think.",
    path: "/quiz",
  });
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});   // { [qid]: choiceIndex }
  const [phase, setPhase] = useState("intro");  // "intro" | "wizard" | "submitting"
  const [guess, setGuess] = useState(null);     // contest: persona slug guessed pre-quiz
  // Soft monthly gate: { persona, member } when this browser/account already
  // has a result this calendar month. Server-side checks remain the authority.
  const [softBlock, setSoftBlock] = useState(() => {
    const rec = readTakenRecord();
    return rec ? { persona: rec.persona, member: false } : null;
  });
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // Anonymous-lead gate state: when the backend returns
  // `email_gate_required` we hold the lead-token + a small persona
  // preview here. Submitting the gate trades the token for the full
  // result via /quiz/claim-result.
  const [leadGate, setLeadGate] = useState(null); // { token, preview } | null

  // The 3·2·1 countdown IS the quiz start for organic visitors — there is
  // no press-to-start button. Paid Google visitors (utm_source=google-ads
  // or a gclid) skip it for the promise-led PaidIntro instead. `?v=classic`
  // falls back to the intro landing; a `?resume=` link goes straight to
  // the saved question. Dismissing the overlay drops back to the intro.
  const [searchParams] = useSearchParams();
  const paid = useMemo(
    () => searchParams.get("utm_source") === "google-ads" || searchParams.has("gclid"),
    [searchParams],
  );
  const [showCountdown, setShowCountdown] = useState(
    () => !paid && searchParams.get("v") !== "classic" && !searchParams.get("resume"),
  );
  // Encouragement beats — a reward card after Q1, Q5, Q10 and Q15, each
  // teasing the next one. Keyed by the question number just answered;
  // auto-advance; each shown once per take. See QUIZ_BEATS.
  const [interstitial, setInterstitial] = useState(null);
  const beatsShownRef = useRef(new Set());

  // ?ref= attribution — captured ONCE on first mount + cached in
  // sessionStorage so a refresh mid-quiz doesn't lose the source. We use
  // useRef so the value is stable across re-renders without triggering
  // submission-effect dependencies.
  const refSource = useRef(null);
  // Canonical question order as served by the backend — submissions must
  // map answers back to this order regardless of the shuffled display.
  const originalOrder = useRef([]);
  if (refSource.current === null) {
    const fromUrl = searchParams.get("ref");
    const fromStorage = (() => {
      try {
        return window.sessionStorage.getItem("quiz_ref");
      } catch {
        return null;
      }
    })();
    refSource.current = fromUrl || fromStorage || null;
    if (fromUrl) {
      try {
        window.sessionStorage.setItem("quiz_ref", fromUrl);
      } catch {
        // sessionStorage may be unavailable (private mode); fall through.
      }
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/quiz/questions");
        if (!cancelled) {
          originalOrder.current = r.data.questions;
          setQuestions(shuffleForDisplay(r.data.questions));
        }
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Members may retake anytime — the monthly soft gate only applies to
  // anonymous visitors (server-side lead checks remain the authority).
  const { user } = useAuth();
  const blocked = Boolean(softBlock && !user);

  // Remember completion locally (layer-1 soft gate for anonymous retakes).
  useEffect(() => {
    if (!result?.name) return;
    try {
      localStorage.setItem(TAKEN_KEY, JSON.stringify({
        month: new Date().toISOString().slice(0, 7),
        persona: result.name,
      }));
    } catch {
      // storage unavailable — server-side checks still apply
    }
  }, [result]);

  // "Human proof" beacon — bots and in-app preloads load the page but
  // never scroll, tap, or type. First interaction of any kind = engage.
  useEffect(() => {
    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      sendQuizFunnelEvent("engage");
      window.removeEventListener("pointerdown", fire);
      window.removeEventListener("scroll", fire);
      window.removeEventListener("keydown", fire);
    };
    window.addEventListener("pointerdown", fire, { passive: true });
    window.addEventListener("scroll", fire, { passive: true });
    window.addEventListener("keydown", fire);
    return () => {
      window.removeEventListener("pointerdown", fire);
      window.removeEventListener("scroll", fire);
      window.removeEventListener("keydown", fire);
    };
  }, []);

  const currentQ = questions[currentIdx];
  const selected = currentQ ? answers[currentQ.id] : undefined;
  const isLast = currentIdx === questions.length - 1;
  // Progress reflects how many questions the user has actually answered so
  // far — not just which question they've scrolled to. Honest signal of
  // "how much is done."
  const answeredCount = Object.keys(answers).length;
  const progressPct = questions.length
    ? Math.round((answeredCount / questions.length) * 100)
    : 0;

  // ---------------- Exit-intent rescue modal ----------------
  // Fires up to MAX_EXIT_SHOWS times per visit, and only mid-questions
  // (never on the intro, interstitials, email gate, or result). Triggers:
  // desktop mouse leaving through the top, mobile back button, or 25s of
  // inactivity.
  const MAX_EXIT_SHOWS = 3;
  const [exitModal, setExitModal] = useState(null); // { trigger } | null
  const exitCountRef = useRef(0);
  const backGuardRef = useRef(false);

  const openExitModal = useCallback((trigger) => {
    if (exitCountRef.current >= MAX_EXIT_SHOWS) return;
    exitCountRef.current += 1;
    setExitModal({ trigger });
  }, []);

  const exitEligible =
    phase === "wizard" && !interstitial && !leadGate && !result &&
    !exitModal && questions.length > 0 && exitCountRef.current < MAX_EXIT_SHOWS;

  // Desktop: pointer leaves through the top of the viewport.
  useEffect(() => {
    if (!exitEligible) return undefined;
    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 8) openExitModal("mouse_leave");
    };
    document.addEventListener("mouseout", onMouseOut);
    return () => document.removeEventListener("mouseout", onMouseOut);
  }, [exitEligible, openExitModal]);

  // Mobile: back button mid-quiz. One guard history entry is pushed when
  // questions begin; each back press re-pushes it and opens the modal,
  // until the show budget runs out — then back works normally.
  useEffect(() => {
    if (!exitEligible) return undefined;
    if (!backGuardRef.current) {
      backGuardRef.current = true;
      window.history.pushState({ fhQuizGuard: true }, "");
    }
    const onPop = () => {
      window.history.pushState({ fhQuizGuard: true }, "");
      openExitModal("back_button");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [exitEligible, openExitModal]);

  // Stall detector: 25s on the same question without answering.
  useEffect(() => {
    if (!exitEligible) return undefined;
    const t = setTimeout(() => openExitModal("idle"), 25_000);
    return () => clearTimeout(t);
  }, [exitEligible, currentIdx, answeredCount, openExitModal]);

  const handleSelect = (choiceIdx) => {
    // First answer ever = the human-verified "real quiz start" signal.
    if (Object.keys(answers).length === 0) sendQuizFunnelEvent("q1");
    setAnswers((prev) => ({ ...prev, [currentQ.id]: choiceIdx }));
  };

  const handleNext = async () => {
    if (selected === undefined) return;
    if (!isLast) {
      // Reward beat for the question they just answered (1, 5, 10, 15).
      const beatKey = currentIdx + 1;
      const beat = QUIZ_BEATS[beatKey];
      if (beat && !beatsShownRef.current.has(beatKey)) {
        beatsShownRef.current.add(beatKey);
        if (beat.funnelStep) sendQuizFunnelEvent(beat.funnelStep);
        setInterstitial(beatKey);
      }
      setCurrentIdx((i) => i + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // Last question → submit
    setPhase("submitting");
    setError(null);
    try {
      const ordered = originalOrder.current.map((q) => answers[q.id]);
      const r = await api.post("/quiz/submit", {
        answers: ordered,
        ref: refSource.current,
        guess,
        ...quizIdentity(),
      });
      sendQuizFunnelEvent("complete");
      // Anonymous path → soft gate. The persona identity is revealed
      // instantly on the QuizSoftReveal page; the inline email form
      // there calls /quiz/claim-result and feeds `setResult` itself.
      if (r.data?.email_gate_required) {
        sendQuizFunnelEvent("gate_shown");
        setLeadGate({
          token: r.data.lead_token,
          preview: r.data.persona_preview || null,
        });
        setPhase("wizard");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      setResult(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Meta Pixel — MoneyMind Quiz is the top-of-funnel lead magnet,
      // so a completed submission is a Lead event for FB Ads
      // optimisation + lookalike audiences. Fire AFTER backend success
      // so we don't credit abandoned attempts.
      try {
        const { trackLead } = await import("../lib/metaPixel");
        trackLead({ content_name: "MoneyMind Quiz", content_category: "lead-magnet" });
      } catch {
        // pixel unavailable — quiz flow continues normally
      }
      try {
        const { trackQuizLeadConversion } = await import("../lib/googleAds");
        trackQuizLeadConversion();
      } catch {
        // google tag unavailable — no-op
      }

      // "We've turned on a starter habit for you" toast.
      // Only fires when the backend auto-enabled a habit (authed users with
      // a known persona slug). Lives in sonner so it dismisses on its own
      // and stacks cleanly with any other notifications.
      const auto = r.data?.auto_enabled_habit;
      if (auto?.slug && auto?.title) {
        toast(`We've turned on a starter habit for you`, {
          description: `"${auto.title}" — tailored to your persona. Open the Habit Loop to begin.`,
          duration: 10_000,
          action: {
            label: "Open Habit Loop",
            onClick: () => { window.location.href = "/app/habits"; },
          },
          // testid for the regression test — sonner doesn't expose a
          // first-class data-testid, but the toast role + text content
          // is enough for end-to-end QA to assert on.
        });
      }
    } catch (e) {
      setError(formatApiError(e));
      setPhase("wizard");
    }
  };

  const handleBack = () => {
    if (currentIdx > 0) {
      setCurrentIdx((i) => i - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Called by the inline email gate on QuizSoftReveal after a successful
  // /quiz/claim-result — this is the "lead captured" moment, so the Meta
  // Pixel + Google Ads Lead conversions fire here (not on the soft reveal).
  const handleGateResult = (claimed) => {
    sendQuizFunnelEvent("lead", { persona: claimed?.slug || null });
    setLeadGate(null);
    setResult(claimed);
    window.scrollTo({ top: 0, behavior: "smooth" });
    (async () => {
      try {
        const { trackLead } = await import("../lib/metaPixel");
        trackLead({ content_name: "MoneyMind Quiz", content_category: "lead-magnet" });
      } catch {
        // pixel unavailable — no-op
      }
      try {
        const { trackQuizLeadConversion } = await import("../lib/googleAds");
        trackQuizLeadConversion();
      } catch {
        // google tag unavailable — no-op
      }
    })();
  };

  // ---------------- Save-my-spot + magic-link resume ----------------
  const handleSaveSpot = async (email) => {
    try {
      await api.post("/public/quiz-save-progress", {
        email,
        answers,
        question_index: currentIdx,
        guess,
        ref: refSource.current,
        total: questions.length || 20,
      });
    } catch (e) {
      throw new Error(formatApiError(e));
    }
  };

  // ?resume={token} — restore a saved take once questions have loaded.
  // Position lands on the first unanswered question in THIS visit's
  // shuffle (answers are keyed by canonical question id, so they map
  // cleanly across shuffles).
  const resumeToken = searchParams.get("resume");
  const resumeAppliedRef = useRef(false);
  useEffect(() => {
    if (!resumeToken || resumeAppliedRef.current || !questions.length) return;
    resumeAppliedRef.current = true;
    (async () => {
      try {
        const r = await api.get(`/public/quiz-resume/${resumeToken}`);
        const restored = {};
        for (const q of originalOrder.current) {
          const v = r.data.answers?.[String(q.id)];
          if (typeof v === "number") restored[q.id] = v;
        }
        setAnswers(restored);
        const firstUnanswered = questions.findIndex(
          (q) => restored[q.id] === undefined,
        );
        setCurrentIdx(firstUnanswered === -1 ? questions.length - 1 : firstUnanswered);
        if (r.data.guess) setGuess(r.data.guess);
        if (r.data.ref && !refSource.current) refSource.current = r.data.ref;
        setSoftBlock(null);
        setPhase("wizard");
        sendQuizFunnelEvent("start", { mode: "resume" });
        toast("Welcome back — your progress is restored.", {
          description: "Pick up right where you left off.",
        });
      } catch {
        toast("That saved link has expired.", {
          description: "No worries — you can start fresh below.",
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeToken, questions]);

  const handleRetake = () => {
    setAnswers({});
    setResult(null);
    setCurrentIdx(0);
    setInterstitial(null);
    beatsShownRef.current = new Set();
    // Fresh shuffle on retake — same anti-memorization guarantee.
    if (originalOrder.current.length) {
      setQuestions(shuffleForDisplay(originalOrder.current));
    }
    // Same-month anonymous retakes hit the friendly monthly gate instead of
    // the intro; members go straight back to the intro.
    const rec = readTakenRecord();
    if (rec && !user) setSoftBlock({ persona: rec.persona, member: false });
    setPhase("intro");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F1E8]">
        <Loader2 className="h-8 w-8 animate-spin text-[#787671]" />
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-[#F4F1E8]">
        <QuizHeader />
        <QuizResult result={result} onRetake={handleRetake} />
        <Footer />
      </div>
    );
  }

  // Soft gate — persona revealed instantly, depth + contest entry behind
  // the inline email form.
  if (leadGate) {
    return (
      <div className="min-h-screen bg-[#F4F1E8] flex flex-col" data-testid="quiz-page">
        <QuizHeader focus />
        <QuizSoftReveal
          preview={leadGate.preview}
          leadToken={leadGate.token}
          onResult={handleGateResult}
        />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F1E8] flex flex-col" data-testid="quiz-page">
      <QuizHeader focus={phase !== "intro"} />

      <main className="flex-1 px-4 sm:px-6 py-10">
        {blocked && phase === "intro" ? (
          <SoftGatePanel
            block={softBlock}
            onOverride={() => {
              try { localStorage.removeItem(TAKEN_KEY); } catch { /* noop */ }
              setSoftBlock(null);
            }}
          />
        ) : phase === "intro" ? (
          <Intro
            paid={paid}
            onPick={(slug) => {
              setGuess(slug);
              sendQuizFunnelEvent("start", { mode: "guess" });
              setPhase("wizard");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            onSkip={() => {
              sendQuizFunnelEvent("start", { mode: "skip" });
              setPhase("wizard");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        ) : interstitial ? (
          <EncouragementCard
            beatKey={interstitial}
            onDone={() => setInterstitial(null)}
          />
        ) : (
          <Wizard
            question={currentQ}
            index={currentIdx}
            total={questions.length}
            answeredCount={answeredCount}
            progressPct={progressPct}
            selected={selected}
            onSelect={handleSelect}
            onBack={handleBack}
            onNext={handleNext}
            isLast={isLast}
            submitting={phase === "submitting"}
            error={error}
          />
        )}
      </main>

      <Footer />
      {showCountdown && phase === "intro" && !blocked && !result && (
        <CountdownStart
          personas={PERSONA_OPTIONS}
          guess={guess}
          onGuess={setGuess}
          onStart={(mode) => {
            setShowCountdown(false);
            sendQuizFunnelEvent("start", { mode });
            setPhase("wizard");
            window.scrollTo(0, 0);
          }}
          onDismiss={() => setShowCountdown(false)}
        />
      )}
      {exitModal && (
        <ExitRescueModal
          trigger={exitModal.trigger}
          remaining={Math.max(questions.length - answeredCount, 1)}
          answered={answeredCount}
          total={questions.length}
          questionIndex={currentIdx + 1}
          onClose={() => setExitModal(null)}
          onSaveSpot={handleSaveSpot}
        />
      )}
    </div>
  );
}


// ---------------- Header / Footer ----------------

function QuizHeader({ focus = false }) {
  const brand = (
    <>
      <TrendingUp className="h-5 w-5" style={{ color: GOLD }} />
      <span className="hidden sm:inline">Financial Habits to Succeed</span>
      <span className="sm:hidden">Finhabits</span>
    </>
  );
  return (
    <header
      className="w-full px-4 sm:px-8 py-3 flex items-center justify-between"
      style={{ background: NAVY }}
      data-testid="quiz-header"
    >
      {focus ? (
        /* Focus mode — no exit ramp mid-quiz. Brand stays visible but inert. */
        <span
          className="flex items-center gap-2 text-white text-sm sm:text-base font-medium"
          data-testid="quiz-home-brand"
        >
          {brand}
        </span>
      ) : (
        <Link
          to="/"
          className="flex items-center gap-2 text-white text-sm sm:text-base font-medium hover:opacity-90"
          data-testid="quiz-home-link"
        >
          {brand}
        </Link>
      )}
      <div className="flex items-center gap-2 text-white text-sm sm:text-base">
        <HelpCircle className="h-5 w-5" style={{ color: GOLD }} />
        <span>MoneyMind Quiz&trade;</span>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer
      className="w-full px-4 sm:px-8 py-3 text-center text-xs text-white"
      style={{ background: NAVY }}
    >
      Made by Finhabits
    </footer>
  );
}


// ---------------- Monthly soft gate ----------------

function nextMonthLabel() {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return next.toLocaleDateString("en-US", { month: "long", day: "numeric" });
}

function SoftGatePanel({ block, onOverride }) {
  return (
    <div className="max-w-xl mx-auto text-center bg-white rounded-xl shadow-md p-8 sm:p-12" data-testid="quiz-monthly-gate">
      <span className="text-4xl" aria-hidden>📆</span>
      <h1 className="font-serif text-3xl sm:text-4xl mt-4 text-[#1B2A3A]">
        You&apos;ve already taken this month&apos;s quiz
      </h1>
      <p className="mt-4 text-[#4d4f4c] leading-relaxed">
        {block.persona ? (
          <>Your MoneyMind is <strong>{block.persona}</strong>. </>
        ) : null}
        To keep the monthly contest fair, it&apos;s one result per person per
        calendar month. Retakes open <strong>{nextMonthLabel()}</strong>.
      </p>
      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/register"
          className="px-6 py-3 rounded-full font-semibold bg-[#D49B3C] text-[#2A1A05] hover:opacity-90 transition-opacity"
          data-testid="quiz-gate-register-cta"
        >
          Put your result to work — join free trial
        </Link>
        <Link
          to="/contest-rules"
          className="px-6 py-3 rounded-full border border-[#E6E3DB] text-[#2C302E] hover:border-[#D49B3C] transition-colors"
        >
          Contest rules
        </Link>
      </div>
      {!block.member && (
        <button
          type="button"
          onClick={onOverride}
          className="mt-6 text-xs text-[#9b9892] underline hover:text-[#2C302E]"
          data-testid="quiz-gate-not-you"
        >
          Not you? Someone else used this device — take the quiz anyway
        </button>
      )}
    </div>
  );
}


// ---------------- Intro card ----------------

function Intro({ onPick, onSkip, paid = false }) {
  return (
    <div className="max-w-2xl mx-auto">
      {/* Pick-first landing — tapping a persona card records the contest
          guess AND starts the quiz in the same tap. Paid visitors get the
          promise-led opener first; the cards become the optional contest. */}
      <section
        className="bg-white rounded-xl shadow-md p-6 sm:p-10 text-center"
        data-testid="quiz-pick-card"
      >
        {paid ? (
          <PaidIntro onStart={onSkip} />
        ) : (
          <>
            <span
              className="inline-block px-4 py-1.5 rounded-full text-[11px] font-semibold tracking-widest uppercase"
              style={{ background: "#FAEACB", color: GOLD_DARK }}
              data-testid="quiz-intro-eyebrow"
            >
              The MoneyMind Quiz&trade;
            </span>
            <h1
              className="font-serif text-3xl sm:text-4xl mt-5 text-[#1B2A3A] leading-tight"
              data-testid="quiz-intro-title"
            >
              Pick the one that feels like you —{" "}
              <span style={{ color: GOLD_DARK }}>then see if you&apos;re right.</span>
            </h1>
            <p className="mt-3 text-sm sm:text-base text-[#4d4f4c]">
              Tap a card to make your guess and start. 20 quick questions reveal
              your real MoneyMind. A quick financial health check built around
              habits &mdash; not math or literacy tests.
            </p>
          </>
        )}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {PERSONA_OPTIONS.map((p) => (
            <button
              key={p.slug}
              type="button"
              onClick={() => onPick(p.slug)}
              className="rounded-lg border border-[#E6E3DB] bg-[#FBFAF7] px-2 py-3.5 text-center transition-colors hover:border-[#D49B3C] hover:bg-[#FAEACB]"
              data-testid={`quiz-pick-${p.slug}`}
            >
              <span className="block text-2xl" aria-hidden>{p.icon}</span>
              <span className="block mt-1 text-xs font-semibold leading-tight text-[#2C302E]">{p.name}</span>
              <span className="block mt-0.5 text-[10px] text-[#9b9892]">{p.hint}</span>
            </button>
          ))}
        </div>
        {!paid && (
          <button
            type="button"
            onClick={onSkip}
            className="mt-6 inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold transition-opacity hover:opacity-90"
            style={{ background: GOLD, color: "#2A1A05" }}
            data-testid="quiz-intro-start"
          >
            Not sure? Just start
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-xs text-[#4d4f4c]">
          <span className="inline-flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" style={{ color: GOLD }} /> 20 questions
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" style={{ color: GOLD }} /> ~5 minutes
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" style={{ color: GOLD }} /> Free
          </span>
        </div>
        <p className="mt-4 text-[11px] text-[#9b9892] max-w-md mx-auto" data-testid="quiz-email-note">
          See your persona instantly — your email unlocks the full breakdown,
          your 60-day free pass to the app + contest entry.
        </p>
        <p className="mt-2 text-[11px] text-[#9b9892] max-w-md mx-auto" data-testid="quiz-contest-note">
          Guess right and you&apos;re entered to win a{" "}
          <strong>full year of membership free</strong> ($120 value) — one
          winner drawn each month. No purchase necessary.{" "}
          <Link to="/contest-rules" className="underline hover:text-[#2C302E]" data-testid="quiz-contest-rules-link">
            Contest rules
          </Link>
        </p>
        <p className="mt-2 text-[11px] text-[#9b9892]">
          For informational purposes only. Not financial advice.
        </p>
      </section>

      {/* Below the fold — fuller pitch for readers & search engines */}
      <section
        className="mt-6 bg-white rounded-xl shadow-md p-6 sm:p-8 text-center"
        data-testid="quiz-intro-card"
      >
        <h2 className="font-serif text-2xl sm:text-3xl text-[#1B2A3A]">
          What you&apos;ll get
        </h2>
        <p className="mt-4 text-sm sm:text-base text-[#4d4f4c] leading-relaxed max-w-lg mx-auto">
          No right answers — just yours. On the other side, you&apos;ll get your
          MoneyMind&trade; persona, a snapshot of your strengths and growth
          edges, and three habits tuned to how you actually think about money.
        </p>
      </section>

      {/* Trust badges */}
      <section
        className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4"
        data-testid="quiz-trust-badges"
      >
        <TrustBadge icon={ShieldCheck} title="Science-Backed" body="Grounded in established behavioral-science research." />
        <TrustBadge icon={Lock} title="100% Private" body="Your answers stay on your account only." />
        <TrustBadge icon={Zap} title="Instant Results" body="See your persona the moment you finish." />
      </section>
    </div>
  );
}

function TrustBadge({ icon: Icon, title, body }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 text-center">
      <div
        className="inline-flex items-center justify-center h-10 w-10 rounded-full mb-2"
        style={{ background: "#FAEACB" }}
      >
        <Icon className="h-5 w-5" style={{ color: GOLD_DARK }} />
      </div>
      <p className="font-semibold text-sm text-[#1B2A3A]">{title}</p>
      <p className="text-xs text-[#787671] mt-1 leading-relaxed">{body}</p>
    </div>
  );
}


// ---------------- Encouragement beats ----------------

const INTERSTITIAL_MS = 4000;

// The guide promised at the Q5 beat. Delivered as a link in the Day-0
// results email (`email_service.render_nurture_email`) — keep the title
// here in step with QUIZ_UNLOCK_GUIDE there.
const UNLOCK_GUIDE_TITLE = "The 5-Minute Weekly Money Check-In";

// One reward card per beat, keyed by the question just answered. Each
// one names the next beat so there's always something ahead.
const QUIZ_BEATS = {
  1: {
    eyebrow: "You're in",
    headline: "That's one down.",
    body: "No right or wrong answers here — just yours. We read your habits, never your math.",
    tease: "Surprise waiting at Q5",
    funnelStep: null, // `q1` already fires on the first answer
  },
  5: {
    eyebrow: "Unlocked",
    headline: "You just unlocked a free guide.",
    body: "Yours to keep — it comes with your results by email.",
    unlock: UNLOCK_GUIDE_TITLE,
    tease: "Surprise waiting at Q10",
    funnelStep: "q5",
  },
  10: {
    eyebrow: "Halfway there",
    headline: "10 down, 10 to go.",
    body: "One free year of membership will be worth it once you complete this challenge.",
    tease: "Surprise waiting at Q15",
    funnelStep: "half",
  },
  15: {
    eyebrow: "Almost yours",
    headline: "5 questions from your result.",
    body: "These last five are the ones that decide it — your MoneyMind, your habit loop, and your 4-week plan are on the other side.",
    tease: "Your result waiting at Q20",
    funnelStep: "q15",
  },
};

function EncouragementCard({ beatKey, onDone }) {
  const beat = QUIZ_BEATS[beatKey];
  useEffect(() => {
    const t = setTimeout(onDone, INTERSTITIAL_MS);
    return () => clearTimeout(t);
  }, [onDone]);
  if (!beat) return null;
  return (
    <div className="max-w-2xl mx-auto" data-testid={`quiz-interstitial-q${beatKey}`}>
      <section className="bg-white rounded-xl shadow-md p-8 sm:p-12 text-center animate-in fade-in zoom-in-95 duration-500">
        <span
          className="inline-block px-4 py-1.5 rounded-full text-[11px] font-semibold tracking-widest uppercase"
          style={{ background: "#FAEACB", color: GOLD_DARK }}
        >
          {beat.eyebrow}
        </span>
        <h2 className="font-serif text-3xl sm:text-4xl mt-5 text-[#1B2A3A] leading-tight">
          {beat.headline}
        </h2>
        <p className="mt-4 text-lg text-[#4d4f4c] leading-relaxed max-w-md mx-auto">
          {beat.body}
        </p>

        {beat.unlock && (
          <div
            className="mt-6 mx-auto max-w-md flex items-start gap-3 text-left rounded-xl px-5 py-4"
            style={{ background: "#F0F5EE", border: "1px solid rgba(96,119,98,0.4)" }}
            data-testid="quiz-beat-unlock"
          >
            <Gift className="h-5 w-5 mt-0.5 shrink-0 text-[#3E6B37]" />
            <div>
              <p className="text-sm font-semibold text-[#1B2A3A]">{beat.unlock}</p>
              <p className="text-xs text-[#4d4f4c] mt-0.5 leading-relaxed">
                Catch the leaks before month-end, in five minutes a week.
              </p>
            </div>
          </div>
        )}

        <p className="mt-6 inline-flex items-center gap-2 text-sm text-[#787671]">
          <Lock className="h-4 w-4" style={{ color: GOLD_DARK }} />
          Your answers are completely private — never shared, never sold.
        </p>

        {/* Auto-advance bar */}
        <div className="mt-7 h-1.5 max-w-xs mx-auto bg-[#F4F1E8] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              background: GOLD,
              animation: `fh-interstitial-fill ${INTERSTITIAL_MS}ms linear forwards`,
            }}
          />
        </div>
        <style>{`@keyframes fh-interstitial-fill { from { width: 0%; } to { width: 100%; } }`}</style>

        <div className="mt-5 flex flex-col items-center gap-3">
          <span
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: "#FAEACB", color: GOLD_DARK }}
            data-testid={`quiz-beat-tease-q${beatKey}`}
          >
            <Sparkles className="h-3.5 w-3.5" />
            {beat.tease}
          </span>
          <button
            type="button"
            onClick={onDone}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#787671] hover:text-[#2C302E] transition-colors"
            data-testid="quiz-interstitial-continue"
          >
            Continue <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    </div>
  );
}


// ---------------- Wizard ----------------

function Wizard({
  question, index, total, answeredCount, progressPct,
  selected, onSelect, onBack, onNext, isLast, submitting, error,
}) {
  const letters = useMemo(() => ["A", "B", "C", "D"], []);
  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress strip */}
      <div className="flex items-center gap-3 text-sm text-[#4d4f4c] mb-3" data-testid="quiz-progress">
        <span className="font-medium">
          Question {index + 1} of {total}
        </span>
        <div className="flex-1 h-2 bg-[#E6E3DB] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${progressPct}%`, background: GOLD }}
          />
        </div>
        <span className="text-xs text-[#9b9892] tabular-nums" data-testid="quiz-progress-pct">
          {progressPct}%
        </span>
      </div>

      {index === 0 && (
        <p className="text-center text-xs text-[#787671] mb-3" data-testid="quiz-gut-note">
          No right or wrong answers — go with your gut.
        </p>
      )}

      {/* Question card */}
      <section
        className="bg-white rounded-xl shadow-md p-8 sm:p-10"
        data-testid={`quiz-question-${question.id}`}
      >
        <p className="text-2xl font-bold text-[#1B2A3A]" data-testid="quiz-question-label">
          Q{index + 1}
        </p>
        <h2 className="font-serif text-2xl sm:text-3xl text-[#1B2A3A] mt-2 leading-snug">
          {question.prompt}
        </h2>

        <div className="mt-6 space-y-3">
          {question.answers.map((a, idx) => {
            const choiceIdx = a.origIdx ?? idx;
            const isSelected = selected === choiceIdx;
            const keyId = `${question.id}-a${choiceIdx}`;
            return (
              <button
                key={keyId}
                type="button"
                onClick={() => onSelect(choiceIdx)}
                className="w-full text-left flex items-start gap-4 p-4 rounded-lg border-2 transition-all"
                style={{
                  borderColor: isSelected ? GOLD : "#E6E3DB",
                  background: isSelected ? "#FAEACB" : "white",
                }}
                data-testid={`quiz-answer-${question.id}-${idx}`}
                aria-pressed={isSelected}
              >
                <span
                  className="inline-flex items-center justify-center h-9 w-9 rounded-full text-base font-semibold shrink-0 transition-colors"
                  style={{
                    background: isSelected ? GOLD : "#F4F1E8",
                    color: isSelected ? "#2A1A05" : "#787671",
                  }}
                >
                  {letters[idx]}
                </span>
                <span className="text-[#2C302E] text-base leading-relaxed pt-1.5">
                  {a.text}
                </span>
              </button>
            );
          })}
        </div>

        {error && (
          <p
            className="mt-5 p-3 bg-[#FFF6F1] border border-[#C05C3D] text-[#C05C3D] text-sm rounded"
            data-testid="quiz-error"
          >
            {error}
          </p>
        )}

        {/* Nav buttons */}
        <div className="mt-8 flex items-center justify-between">
          <button
            onClick={onBack}
            disabled={index === 0 || submitting}
            className="inline-flex items-center gap-1 text-sm text-[#787671] hover:text-[#2C302E] disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="quiz-back"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <button
            onClick={onNext}
            disabled={selected === undefined || submitting}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: GOLD, color: "#2A1A05" }}
            data-testid="quiz-next"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Calculating…
              </>
            ) : isLast ? (
              <>
                Reveal my MoneyMind
                <ArrowRight className="h-4 w-4" />
              </>
            ) : (
              <>
                Next
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </div>
      </section>

      <p className="mt-5 text-center text-xs text-[#9b9892]">
        Answered {answeredCount} of {total} · You can change any answer using Back.
      </p>
    </div>
  );
}
